import * as THREE from 'three';
import { box, weld } from '../level/VoxelFactory.js';
import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import {
  PLAYER, TILE_X, ROW_DEPTH, FIELD_HALF_TILES, ROW, COLORS,
  JUICE, CURRY,
} from '../core/Constants.js';

import { DeathCause } from '../systems/CollisionSystem.js';

const X_LIMIT = FIELD_HALF_TILES * TILE_X;
const PLAYER_SCALE = 1.0;

function buildManGeometry() {
  const S = COLORS.SKIN, H = COLORS.HAIR, T = COLORS.SHIRT, P = COLORS.TROUSERS;
  const parts = [
    // feet + legs
    box(0.42, 0.18, 0.58, COLORS.SHOE, -0.28, 0.09, -0.04),
    box(0.42, 0.18, 0.58, COLORS.SHOE, 0.28, 0.09, -0.04),
    box(0.38, 0.64, 0.42, P, -0.28, 0.5, 0),
    box(0.38, 0.64, 0.42, P, 0.28, 0.5, 0),
    box(1.02, 0.3, 0.52, P, 0, 0.95, 0),
    // torso + arms
    box(1.02, 0.76, 0.56, T, 0, 1.46, 0),
    box(0.26, 0.7, 0.36, T, -0.64, 1.42, 0),
    box(0.26, 0.7, 0.36, T, 0.64, 1.42, 0),
    box(0.26, 0.22, 0.36, S, -0.64, 0.99, 0),
    box(0.26, 0.22, 0.36, S, 0.64, 0.99, 0),
    // head
    box(0.32, 0.14, 0.32, S, 0, 1.91, 0),
    box(0.78, 0.68, 0.68, S, 0, 2.3, 0),
    // hair — covers the crown, back and sides, leaving the face clear
    box(0.86, 0.24, 0.76, H, 0, 2.7, 0),
    box(0.86, 0.52, 0.16, H, 0, 2.4, 0.32),
    box(0.1, 0.46, 0.72, H, -0.42, 2.38, 0),
    box(0.1, 0.46, 0.72, H, 0.42, 2.38, 0),
  ];
  return weld(parts);
}

function buildChildGeometry(shirtColor = 0xf59e0b) {
  const S = COLORS.SKIN, H = COLORS.HAIR, P = COLORS.TROUSERS;
  const T = shirtColor;
  const parts = [
    // feet + legs
    box(0.32, 0.14, 0.44, COLORS.SHOE, -0.22, 0.07, -0.04),
    box(0.32, 0.14, 0.44, COLORS.SHOE, 0.22, 0.07, -0.04),
    box(0.28, 0.48, 0.32, P, -0.22, 0.38, 0),
    box(0.28, 0.48, 0.32, P, 0.22, 0.38, 0),
    box(0.78, 0.22, 0.40, P, 0, 0.72, 0),
    // torso + arms
    box(0.78, 0.58, 0.42, T, 0, 1.10, 0),
    box(0.20, 0.52, 0.28, T, -0.48, 1.06, 0),
    box(0.20, 0.52, 0.28, T, 0.48, 1.06, 0),
    box(0.20, 0.16, 0.28, S, -0.48, 0.74, 0),
    box(0.20, 0.16, 0.28, S, 0.48, 0.74, 0),
    // head
    box(0.24, 0.10, 0.24, S, 0, 1.44, 0),
    box(0.62, 0.52, 0.52, S, 0, 1.74, 0),
    // hair
    box(0.68, 0.18, 0.58, H, 0, 2.05, 0),
    box(0.68, 0.40, 0.12, H, 0, 1.82, 0.25),
    box(0.08, 0.35, 0.55, H, -0.34, 1.80, 0),
    box(0.08, 0.35, 0.55, H, 0.34, 1.80, 0),
  ];
  return weld(parts);
}

function buildPoisonManGeometry() {
  const G1 = 0x22c55e, G2 = 0x15803d, G3 = 0x166534, G4 = 0x4ade80;
  const parts = [
    // feet + legs
    box(0.42, 0.18, 0.58, G2, -0.28, 0.09, -0.04),
    box(0.42, 0.18, 0.58, G2, 0.28, 0.09, -0.04),
    box(0.38, 0.64, 0.42, G3, -0.28, 0.5, 0),
    box(0.38, 0.64, 0.42, G3, 0.28, 0.5, 0),
    box(1.02, 0.3, 0.52, G3, 0, 0.95, 0),
    // torso + arms
    box(1.02, 0.76, 0.56, G1, 0, 1.46, 0),
    box(0.26, 0.7, 0.36, G1, -0.64, 1.42, 0),
    box(0.26, 0.7, 0.36, G1, 0.64, 1.42, 0),
    box(0.26, 0.22, 0.36, G4, -0.64, 0.99, 0),
    box(0.26, 0.22, 0.36, G4, 0.64, 0.99, 0),
    // head
    box(0.32, 0.14, 0.32, G4, 0, 1.91, 0),
    box(0.78, 0.68, 0.68, G4, 0, 2.3, 0),
    // hair
    box(0.86, 0.24, 0.76, G2, 0, 2.7, 0),
    box(0.86, 0.52, 0.16, G2, 0, 2.4, 0.32),
    box(0.1, 0.46, 0.72, G2, -0.42, 2.38, 0),
    box(0.1, 0.46, 0.72, G2, 0.42, 2.38, 0),
  ];
  return weld(parts);
}

function buildSkeletonGeometry() {
  const B = 0xf8fafc; // Bone white
  const D = 0x0f172a; // Dark eye sockets

  const parts = [
    // Feet & legs
    box(0.36, 0.14, 0.52, B, -0.28, 0.07, -0.04),
    box(0.36, 0.14, 0.52, B, 0.28, 0.07, -0.04),
    box(0.18, 0.68, 0.18, B, -0.28, 0.48, 0),
    box(0.18, 0.68, 0.18, B, 0.28, 0.48, 0),
    box(0.86, 0.24, 0.46, B, 0, 0.92, 0),
    // Spine & Ribcage
    box(0.24, 0.72, 0.24, B, 0, 1.4, 0),
    box(0.86, 0.1, 0.42, B, 0, 1.2, 0),
    box(0.9, 0.1, 0.44, B, 0, 1.4, 0),
    box(0.86, 0.1, 0.42, B, 0, 1.6, 0),
    // Arms
    box(0.16, 0.68, 0.16, B, -0.62, 1.35, 0),
    box(0.16, 0.68, 0.16, B, 0.62, 1.35, 0),
    // Skull & Eye Sockets
    box(0.74, 0.64, 0.64, B, 0, 2.22, 0),
    box(0.5, 0.14, 0.32, B, 0, 1.84, 0.15),
    box(0.18, 0.18, 0.1, D, -0.2, 2.24, 0.28),
    box(0.18, 0.18, 0.1, D, 0.2, 2.24, 0.28),
    box(0.1, 0.12, 0.1, D, 0, 2.08, 0.28),
  ];
  return weld(parts);
}

export class Player {
  constructor(scene, material, shadowMaterial) {
    this.scene = scene;
    this.material = material;
    this.shadowMaterial = shadowMaterial;

    this.geometry = buildManGeometry();
    this.poisonGeometry = buildPoisonManGeometry();
    this.skeletonGeometry = buildSkeletonGeometry();
    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.scale.setScalar(PLAYER_SCALE);

    this.root = new THREE.Group();
    this.root.add(this.mesh);
    scene.add(this.root);

    this.shadowGeometry = new THREE.BoxGeometry(1.25, 0.04, 1.05);
    this.shadow = new THREE.Mesh(this.shadowGeometry, shadowMaterial);
    this.shadow.renderOrder = 1;
    scene.add(this.shadow);

    this.children = [];
    this.history = [];
    this.childColors = [0xf59e0b, 0x06b6d4, 0xec4899, 0x8b5cf6, 0x10b981, 0xf97316];

    this.hop = null;
    this.queued = null;
    this.isSmooshed = false;
    this.smooshT = 0;
    this.isEnvenomated = false;
    this.envenomT = 0;

    this.unsub = [
      eventBus.on(Events.INPUT_MOVE, (d) => this.requestHop(d)),
    ];
  }

  get goalRow() { return gameState.level.goalRow; }

  syncChildrenCount(count) {
    while (this.children.length < count) {
      const idx = this.children.length;
      const cColor = this.childColors[idx % this.childColors.length];
      const cGeo = buildChildGeometry(cColor);
      const cMesh = new THREE.Mesh(cGeo, this.material);

      const cRoot = new THREE.Group();
      cRoot.add(cMesh);
      this.scene.add(cRoot);

      const cShadowGeo = new THREE.BoxGeometry(0.9, 0.04, 0.8);
      const cShadow = new THREE.Mesh(cShadowGeo, this.shadowMaterial);
      cShadow.renderOrder = 1;
      this.scene.add(cShadow);

      this.children.push({
        root: cRoot,
        mesh: cMesh,
        shadow: cShadow,
        geometry: cGeo,
        shadowGeometry: cShadowGeo,
      });
    }

    for (let i = 0; i < this.children.length; i++) {
      const active = i < count;
      this.children[i].root.visible = active;
      this.children[i].shadow.visible = active;
    }
  }

  removeChild(childIndex, cause = DeathCause.STRUCK, x = 0, row = 0, lane = null, consist = null) {
    const count = gameState.run.childrenCount || 0;
    if (childIndex < 0 || childIndex >= count) return;

    const child = this.children[childIndex];
    if (child) {
      child.root.removeFromParent();
      child.shadow.removeFromParent();
      child.geometry.dispose();
      child.shadowGeometry.dispose();
      this.children.splice(childIndex, 1);
    }

    if (this.history.length > childIndex) {
      this.history.splice(childIndex, 1);
    }

    gameState.run.childrenCount = Math.max(0, count - 1);
    this.syncChildrenCount(gameState.run.childrenCount);

    eventBus.emit(Events.BURST, { x, row, cause, lane, consist });
    eventBus.emit(Events.SHAKE, JUICE.SHAKE_DEATH * 0.5);
  }

  getChildrenPositions() {
    const count = gameState.run.childrenCount || 0;
    const pos = [{ x: gameState.player.x, row: gameState.player.row }];
    for (let i = 0; i < count; i++) {
      const p = this.history[i];
      if (p) {
        pos.push({ x: p.x, row: p.row });
      }
    }
    return pos;
  }

  spawn() {
    const p = gameState.player;
    p.spicyImmunityUntil = 0;
    p.curryPoisonUntil = 0;
    this.hop = null;
    this.queued = null;
    this.isSmooshed = false;
    this.smooshT = 0;
    this.isEnvenomated = false;
    this.envenomT = 0;
    this.mesh.geometry = this.geometry;
    this.root.position.set(p.x, PLAYER.GROUND_Y, 0);
    this.root.rotation.y = 0;
    this.mesh.scale.setScalar(PLAYER_SCALE);
    this.mesh.visible = true;
    this.shadow.visible = true;

    const count = gameState.run.childrenCount || 0;
    this.syncChildrenCount(count);

    this.history = [];
    for (let k = 0; k <= count + 10; k++) {
      this.history[k] = { x: p.x, row: p.row - (k + 1), y: PLAYER.GROUND_Y, rotationY: 0 };
    }

    for (let i = 0; i < count; i++) {
      const c = this.children[i];
      const zPos = (i + 1) * ROW_DEPTH;
      c.root.position.set(p.x, PLAYER.GROUND_Y, zPos);
      c.root.rotation.y = 0;
      c.mesh.scale.setScalar(1.0);
      c.mesh.visible = true;
      c.shadow.visible = true;
      c.shadow.position.set(p.x, PLAYER.GROUND_Y + 0.02, zPos);
    }

    this.syncShadow();
  }

  triggerSmoosh(cause) {
    const count = gameState.run.childrenCount || 0;

    if (cause === DeathCause.COBRA) {
      this.isEnvenomated = true;
      this.envenomT = 0;
      this.isSmooshed = false;
      this.mesh.visible = true;
      this.shadow.visible = true;
      this.mesh.geometry = this.poisonGeometry;
      for (let i = 0; i < count; i++) {
        this.children[i].mesh.visible = true;
      }
      return;
    }

    this.isEnvenomated = false;
    this.isSmooshed = true;
    this.smooshT = 0;
    if (cause === DeathCause.STRUCK) {
      this.mesh.visible = false;
      this.shadow.visible = false;
      for (let i = 0; i < count; i++) {
        this.children[i].mesh.visible = false;
        this.children[i].shadow.visible = false;
      }
    } else {
      this.mesh.visible = true;
      this.shadow.visible = true;
    }
  }

  /** The shadow stays on the ground and shrinks as the hop arcs up. */
  syncShadow() {
    const p = gameState.player;
    const ground = this.rowY(this.hop ? this.hop.row : p.row);
    const lift = Math.max(0, this.root.position.y - ground);
    let s = Math.max(0.35, 1 - lift * 0.42);
    if (this.isSmooshed) {
      const t = Math.min(1, this.smooshT / JUICE.SMOOSH_DURATION);
      const easeSquash = Math.pow(t, 0.4);
      s *= (1.0 + 1.25 * easeSquash);
    }
    this.shadow.position.set(this.root.position.x, ground + 0.02, this.root.position.z);
    this.shadow.scale.set(s, 1, s);
    this.shadow.visible = this.mesh.visible;
  }

  /** Nominal standing height for a row, ignoring whether a deck is actually there. */
  rowY(row) {
    const lane = gameState.level.rows[row];
    return lane && lane.type === ROW.FREIGHT ? PLAYER.DECK_Y : PLAYER.GROUND_Y;
  }

  pushBack(dirX = 0, pushRows = 1) {
    const p = gameState.player;
    if (!gameState.game.isPlaying || p.dead) return;

    // Grant temporary invulnerability so continuous collision does not repeat
    p.invulnUntil = gameState.time + 0.8;

    const targetRow = Math.max(0, p.row - pushRows);
    let targetX = p.x + (dirX || 0) * TILE_X * 0.75;
    targetX = Math.max(-X_LIMIT, Math.min(X_LIMIT, targetX));

    const rotY = dirX !== 0 ? (dirX > 0 ? -Math.PI / 2 : Math.PI / 2) : 0;

    // Save leader position to history buffer for children to follow
    this.history.unshift({
      x: p.x,
      row: p.row,
      y: this.rowY(p.row),
      rotationY: rotY,
    });
    if (this.history.length > 50) this.history.pop();

    this.hop = {
      t: 0,
      fromX: p.x,
      toX: targetX,
      fromZ: -p.row * ROW_DEPTH,
      toZ: -targetRow * ROW_DEPTH,
      fromY: this.root.position.y,
      toY: this.rowY(targetRow),
      row: targetRow,
    };
    this.queued = null;
    p.carrier = null;
    this.root.rotation.y = rotY;

    eventBus.emit(Events.PLAYER_BLOCKED);
    eventBus.emit(Events.SHAKE, JUICE.SHAKE_DEATH * 0.4);
  }

  requestHop({ dx, dz }) {
    const p = gameState.player;
    if (!gameState.game.isPlaying || p.dead) return;

    if (this.hop) {
      this.queued = { dx, dz };
      return;
    }
    this.startHop(dx, dz);
  }

  startHop(dx, dz) {
    const p = gameState.player;
    const toRow = Math.max(0, Math.min(this.goalRow, p.row - dz));
    let toX = p.x + dx * TILE_X;

    if (dx !== 0 && (toX > X_LIMIT + 0.01 || toX < -X_LIMIT - 0.01)) {
      eventBus.emit(Events.PLAYER_BLOCKED);
      return;
    }
    if (dz !== 0 && toRow === p.row) {
      eventBus.emit(Events.PLAYER_BLOCKED);
      return;
    }
    toX = Math.max(-X_LIMIT, Math.min(X_LIMIT, toX));

    const rotY = Math.atan2(-dx, -dz);

    // Save leader position to history buffer for children to follow
    this.history.unshift({
      x: p.x,
      row: p.row,
      y: this.rowY(p.row),
      rotationY: rotY,
    });

    if (this.history.length > 50) this.history.pop();

    this.hop = {
      t: 0,
      fromX: p.x, toX,
      fromZ: -p.row * ROW_DEPTH, toZ: -toRow * ROW_DEPTH,
      fromY: this.root.position.y, toY: this.rowY(toRow),
      row: toRow,
    };
    p.carrier = null;
    this.root.rotation.y = rotY;
    eventBus.emit(Events.PLAYER_HOP);
  }

  update(dt) {
    const p = gameState.player;
    const count = gameState.run.childrenCount || 0;

    if (this.isEnvenomated) {
      this.envenomT += dt;
      const t = this.envenomT;

      if (t < 0.45) {
        this.mesh.geometry = this.poisonGeometry;
        const pulse = 1.0 + Math.sin(t * 35) * 0.12;
        this.mesh.scale.set(pulse, 1.0, pulse);
      } else {
        this.mesh.geometry = this.skeletonGeometry;
        this.mesh.scale.setScalar(PLAYER_SCALE);
        this.root.rotation.y += Math.sin(t * 22) * 0.06;
      }

      this.syncShadow();
      return;
    }

    if (this.isSmooshed) {
      this.smooshT += dt;
      const duration = JUICE.SMOOSH_DURATION;
      const t = Math.min(1, this.smooshT / duration);
      const easeSquash = Math.pow(t, 0.4);
      const sy = PLAYER_SCALE * Math.max(0.05, 1.0 - 0.95 * easeSquash);
      const sxz = PLAYER_SCALE * (1.0 + 1.25 * easeSquash);

      this.mesh.scale.set(sxz, sy, sxz);
      const ground = this.rowY(p.row);
      this.root.position.y = ground;

      if (t >= 1) {
        this.mesh.visible = false;
        for (let i = 0; i < count; i++) {
          this.children[i].mesh.visible = false;
        }
      }
      this.syncShadow();
      return;
    }

    if (p.dead) return;

    const isCurryPoisoned = gameState.time < p.curryPoisonUntil;
    const isSpicyImmune = gameState.time < p.spicyImmunityUntil;

    if (isCurryPoisoned && !this.isEnvenomated && !this.isSmooshed) {
      this.mesh.geometry = this.poisonGeometry;
    } else if (!isCurryPoisoned && !this.isEnvenomated && !this.isSmooshed) {
      this.mesh.geometry = this.geometry;
    }

    if (this.hop) {
      const h = this.hop;
      h.t += dt;
      const baseHopDur = PLAYER.HOP_DURATION * (isCurryPoisoned ? (CURRY.SLOW_FACTOR || 1.8) : 1.0);
      const t = Math.min(1, h.t / baseHopDur);
      const arc = Math.sin(Math.PI * t);

      this.root.position.x = h.fromX + (h.toX - h.fromX) * t;
      this.root.position.z = h.fromZ + (h.toZ - h.fromZ) * t;
      this.root.position.y = h.fromY + (h.toY - h.fromY) * t + arc * PLAYER.HOP_HEIGHT;

      const scalePulse = isSpicyImmune ? (1.0 + Math.sin(gameState.time * 24.0) * 0.15) : 1.0;
      this.mesh.scale.set(
        (PLAYER_SCALE - 0.16 * arc) * scalePulse,
        (PLAYER_SCALE + 0.26 * arc) * scalePulse,
        (PLAYER_SCALE - 0.16 * arc) * scalePulse,
      );

      // Animate trailing children in sync with leader (1 move behind per child)
      for (let i = 0; i < count; i++) {
        const child = this.children[i];
        const toPos = this.history[i];
        const fromPos = this.history[i + 1] || toPos;

        const childT = t; // synchronized hop arc with leader
        const childArc = Math.sin(Math.PI * childT);

        const cX = fromPos.x + (toPos.x - fromPos.x) * childT;
        const cFromZ = -fromPos.row * ROW_DEPTH;
        const cToZ = -toPos.row * ROW_DEPTH;
        const cZ = cFromZ + (cToZ - cFromZ) * childT;
        const cFromY = fromPos.y || PLAYER.GROUND_Y;
        const cToY = toPos.y || PLAYER.GROUND_Y;
        const cY = cFromY + (cToY - cFromY) * childT + childArc * PLAYER.HOP_HEIGHT * 0.8;

        child.root.position.set(cX, cY, cZ);
        child.root.rotation.y = toPos.rotationY;
        child.mesh.scale.set(1.0 - 0.12 * childArc, 1.0 + 0.2 * childArc, 1.0 - 0.12 * childArc);

        const cGround = this.rowY(childT > 0.5 ? toPos.row : fromPos.row);
        const cLift = Math.max(0, cY - cGround);
        const cs = Math.max(0.3, 1 - cLift * 0.4);
        child.shadow.position.set(cX, cGround + 0.02, cZ);
        child.shadow.scale.set(cs, 1, cs);
      }

      if (t >= 1) {
        p.x = h.toX;
        p.row = h.row;
        this.hop = null;
        this.mesh.scale.setScalar(PLAYER_SCALE);
        if (p.row > p.maxRow) {
          p.maxRow = p.row;
          eventBus.emit(Events.PLAYER_ADVANCE, { row: p.row });
        }
        eventBus.emit(Events.PLAYER_LAND, { row: p.row });
        if (this.queued) {
          const q = this.queued;
          this.queued = null;
          this.startHop(q.dx, q.dz);
        }
      }
    } else {
      // Standing still: flatcar under foot carries leader & children sideways
      if (p.carrier) {
        const delta = p.carrier.lane.dir * p.carrier.lane.speed * dt;
        p.x += delta;
        for (let k = 0; k < this.history.length; k++) {
          this.history[k].x += delta;
        }
      }
      this.root.position.x = p.x;
      this.root.position.z = -p.row * ROW_DEPTH;
      this.root.position.y = this.rowY(p.row);

      // Update active children positions while standing
      for (let i = 0; i < count; i++) {
        const child = this.children[i];
        const pos = this.history[i] || { x: p.x, row: p.row - (i + 1), rotationY: 0 };
        const cZ = -pos.row * ROW_DEPTH;
        const cY = this.rowY(pos.row);

        child.root.position.set(pos.x, cY, cZ);
        child.root.rotation.y = pos.rotationY;
        child.mesh.scale.set(1, 1, 1);

        child.shadow.position.set(pos.x, cY + 0.02, cZ);
        child.shadow.scale.set(1, 1, 1);
      }
    }

    this.syncShadow();

    // Respawn invulnerability blink
    const invuln = gameState.time < p.invulnUntil;
    const vis = !invuln || Math.sin(gameState.time * Math.PI * 2 * PLAYER.BLINK_HZ) > 0;
    this.mesh.visible = vis;
    this.shadow.visible = vis;
    for (let i = 0; i < count; i++) {
      this.children[i].mesh.visible = vis;
      this.children[i].shadow.visible = vis;
    }
  }

  hide() {
    this.mesh.visible = false;
    this.shadow.visible = false;
    for (let i = 0; i < this.children.length; i++) {
      this.children[i].mesh.visible = false;
      this.children[i].shadow.visible = false;
    }
  }

  dispose() {
    this.unsub.forEach((u) => u());
    this.root.removeFromParent();
    this.shadow.removeFromParent();
    this.geometry.dispose();
    this.shadowGeometry.dispose();

    for (let i = 0; i < this.children.length; i++) {
      this.children[i].root.removeFromParent();
      this.children[i].shadow.removeFromParent();
      this.children[i].geometry.dispose();
      this.children[i].shadowGeometry.dispose();
    }
    this.children.length = 0;
  }
}
